// 取込プラン（中身/TMS・純関数）。CSV → 新規/内容変更/重複 に分類する。Firebase非依存＝Nodeでテスト可。
// ① 取込時重複除外: 既存キーと signature が一致する行は「重複」＝取り込まない（スキップ）。
// ② 変更検知: 同一キーで 運賃・高速代 等（signatureFields）が変わった行は「内容変更」＝更新＋差分を記録。
import { readTable } from './csv.js'
import { VENDORS } from './vendors.js'

// 決定的ハッシュ（FNV-1a 32bit）。複合キーを安全・安定なdocIdに畳む用。
function fnv1a(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

// docId 化。移行（元Firebase）は「素のキー」をidにしている（運行ID=75234 / 管理ID=IO... / 案件番号）。
// アプリ取込もvendorId接頭辞を付けず“素のキー”に揃える＝再取込が「重複作成」でなく「上書き」になる。
// ネイティブID（案件番号/管理ID/運行ID）は可読のまま、'/'等だけ無害化。長い・記号を含む複合キーはハッシュに畳む。
export function docIdFor(vendorId, key) {
  const safe = String(key).replace(/[/\\.#$[\]]/g, '_').replace(/\s+/g, ' ').trim()
  if (safe && safe.length <= 120 && !/[|]/.test(safe)) return safe
  return fnv1a(String(key))
}

// signature: ②で比較する可変項目を安定文字列化。
export function signatureOf(trip, fields) {
  return fields.map((f) => `${f}=${trip[f] == null ? '' : trip[f]}`).join(';')
}
const pick = (obj, fields) => Object.fromEntries(fields.map((f) => [f, obj[f]]))

// 既存値との差分（②の内容変更の中身）。prevValues は signatureFields の前回値。
function diffOf(prevValues, trip, fields) {
  const d = []
  for (const f of fields) {
    const a = prevValues ? prevValues[f] : undefined
    const b = trip[f]
    if (String(a == null ? '' : a) !== String(b == null ? '' : b)) d.push({ field: f, from: a, to: b })
  }
  return d
}

/**
 * 取込プランを作る（純関数）。
 * @param {{vendorId:string, text:string, existing?:Object, ctx?:Object}} p
 *   existing: { [key]: { signature, values } }  ← 既存（Supabase or 前回プラン）から作る
 * @returns {{vendorId, rows, summary, intraDup}}
 */
export function planImport({ vendorId, text, existing = {}, ctx = {} }) {
  const vendor = VENDORS[vendorId]
  if (!vendor) throw new Error(`未知のベンダー: ${vendorId}`)
  const fields = vendor.signatureFields
  const { records } = readTable(text)

  // ファイル内の重複（同一キーが複数回）→ 最後の行を採用し件数だけ記録。
  const byKey = new Map()
  let intraDup = 0
  let blank = 0
  for (const r of records) {
    const key = vendor.keyOf(r)
    if (!key || !key.replace(/\|/g, '').trim()) { blank++; continue }
    if (byKey.has(key)) intraDup++
    byKey.set(key, r)
  }

  const rows = []
  const summary = { total: 0, new: 0, changed: 0, unchanged: 0, error: 0, skipped: 0, warn: 0 }
  for (const [key, r] of byKey) {
    summary.total++
    let trip
    try { trip = vendor.map(r, ctx) } catch (e) {
      summary.error++; rows.push({ key, status: 'error', error: String(e && e.message || e), row: r.__row }); continue
    }
    // ベンダー定義の除外条件（例: ハコベル自社便は日計表から取り込まない）。スキップ＝確定取込の対象外。
    const skipReason = vendor.excludeOf ? vendor.excludeOf(trip, r, ctx) : ''
    if (skipReason) {
      summary.skipped++
      rows.push({ key, docId: docIdFor(vendorId, key), status: 'skipped', reason: skipReason, trip })
      continue
    }
    const sig = signatureOf(trip, fields)
    const docId = docIdFor(vendorId, key)
    const prev = existing[key]
    let status, diff = []
    if (!prev) status = 'new'
    else if (prev.signature === sig) status = 'unchanged'
    else { status = 'changed'; diff = diffOf(prev.values, trip, fields) }
    summary[status]++
    const warn = vendor.warnOf ? vendor.warnOf(trip, r, ctx) : ''
    if (warn) summary.warn++
    rows.push({ key, docId, status, trip, sig, values: pick(trip, fields), diff, warn })
  }
  return { vendorId, vendorLabel: vendor.label, rows, summary: { ...summary, blank, intraDup }, intraDup }
}

// プラン → existing マップ（再取込テスト／連続取込のため）。commit後の状態を模す。
export function toExistingMap(plan) {
  const m = {}
  for (const row of plan.rows) {
    if (row.status === 'error' || row.status === 'skipped') continue
    m[row.key] = { signature: row.sig, values: row.values, docId: row.docId }
  }
  return m
}

// DBの全件 → 当該ベンダーの既存マップ。listDocs はサーバ側 filter を持たないのでここで source を絞る。
// source が空の旧行は残す（再取込の突合を壊さない）。
export function existingMapFromDocs(docs, vendor) {
  const map = {}
  const fields = (vendor && vendor.signatureFields) || []
  const vendorId = vendor && vendor.id
  for (const d of docs || []) {
    if (!d) continue
    if (d.source && vendorId && d.source !== vendorId) continue
    const key = d.sourceKey || d.id
    if (!key) continue
    const values = Object.fromEntries(fields.map((f) => [f, d[f]]))
    map[key] = { signature: d._sig || '', values, docId: d.id }
  }
  return map
}
